/**
 * The audit siblings (FIELDSET §1.7, §2.8, §3.4.1): field names, the live `EntryType` and `LogKind` values, and the
 * `LogKind` map (which sibling row each verb writes, on which register).
 *
 * New register code imports this file. `AUDIT_FIELDS` in `config/registers.ts` (13-16's) is left as it is for the
 * Action Register writers; it lacks `LogKind` and `ActorUPN`, which every new verb writes (D-12.1-05-3, D-13-L-8).
 *
 * The `EntryType` and `LogKind` values are read from `liveColumns.json` (the Incidents Audit Log; `fieldset.test.ts`
 * asserts all five siblings carry the same sets), never retyped. The §3.4.1 map's keys are checked to be live values.
 */

import { liveSiblingChoices } from './live';
import type { RecordRegister } from '../../types/records';

/** Sibling internal names (FIELDSET §2.8). `actorLookupId` is how Graph writes the `Actor` Person column. */
export const SIBLING_FIELDS = {
  title: 'Title',
  parentRef: 'ParentRef',
  actor: 'Actor',
  actorLookupId: 'ActorLookupId',
  entryTimestamp: 'EntryTimestamp',
  entryType: 'EntryType',
  fieldChanged: 'FieldChanged',
  oldValue: 'OldValue',
  newValue: 'NewValue',
  note: 'Note',
  logKind: 'LogKind',
  actorUPN: 'ActorUPN',
} as const;

/** The live column names on every sibling (everything in `SIBLING_FIELDS` except the Graph write alias). */
export const SIBLING_COLUMNS: readonly string[] = Object.entries(SIBLING_FIELDS)
  .filter(([key]) => key !== 'actorLookupId')
  .map(([, name]) => name);

/** The four `EntryType` values, in live order. */
export const ENTRY_TYPES: readonly string[] = liveSiblingChoices('incidents', 'EntryType');

/** The sixteen `LogKind` values, in live order (D-12.1-04-3). */
export const LOG_KINDS: readonly string[] = liveSiblingChoices('incidents', 'LogKind');

/** FIELDSET §4.0 writer codes that write sibling rows. */
export type SiblingWriter = 'TRI' | 'REP' | 'WHS' | 'NOT' | 'HND' | 'CLO' | 'AUD' | 'WBF' | 'C4' | 'CIE';

/** A §3.4.1 target: one of the five registers, or one of the two Oversight-only lists. */
export type SiblingTarget = RecordRegister | 'internalAudits' | 'whistleblowerDisclosures';

export interface LogKindUse {
  readonly registers: readonly SiblingTarget[];
  readonly writers: readonly SiblingWriter[];
}

/**
 * FIELDSET §3.4.1, verbatim: each `LogKind` (on an `EntryType` = `Note` row unless noted), the registers whose G
 * rows need it, and the writers. `Acknowledgement` has no §4 row today and no writer.
 */
export const LOG_KIND_MAP: Readonly<Record<string, LogKindUse>> = {
  Notification: { registers: ['incidents', 'complaints'], writers: ['NOT', 'TRI', 'C4'] },
  InformationRequest: { registers: ['incidents'], writers: ['NOT'] },
  RegulatorRequirement: { registers: ['incidents', 'complaints'], writers: ['NOT', 'HND'] },
  RegulatorDecision: { registers: ['incidents'], writers: ['NOT'] },
  Support: { registers: ['incidents', 'complaints', 'feedback', 'whistleblowerDisclosures'], writers: ['HND', 'WBF'] },
  Consultation: { registers: ['incidents', 'feedback', 'risk'], writers: ['HND'] },
  Review: { registers: ['incidents', 'risk', 'internalAudits'], writers: ['CLO', 'AUD'] },
  ProceduralFairness: { registers: ['incidents', 'complaints', 'whistleblowerDisclosures'], writers: ['HND', 'WBF'] },
  Communication: { registers: ['incidents'], writers: ['CLO'] },
  ReprisalConcern: { registers: ['incidents', 'complaints'], writers: ['HND'] },
  ProgressUpdate: { registers: ['complaints'], writers: ['HND'] },
  Disclosure: { registers: ['complaints', 'feedback'], writers: ['HND'] },
  SitePreservation: { registers: ['incidents'], writers: ['WHS'] },
  RTW: { registers: ['incidents'], writers: ['WHS'] },
  AuditMeeting: { registers: ['internalAudits'], writers: ['AUD'] },
  Acknowledgement: { registers: [], writers: [] },
};

/**
 * FIELDSET §3.4.1's two `EntryType` rows (no `LogKind`): `ActionTaken` (HND; CIE on CI) and `InvestigationStep`
 * (Incidents only; the investigator's UPN in `ActorUPN`, position and dates in `Note`).
 */
export const ENTRY_TYPE_MAP: Readonly<Record<string, LogKindUse>> = {
  ActionTaken: { registers: ['incidents', 'complaints', 'feedback', 'ci', 'internalAudits'], writers: ['HND', 'CIE'] },
  InvestigationStep: { registers: ['incidents'], writers: ['HND'] },
};

/** The `LogKind`s a register's verbs may write (from the §3.4.1 map). */
export function logKindsFor(register: RecordRegister): readonly string[] {
  return Object.entries(LOG_KIND_MAP)
    .filter(([, use]) => use.registers.includes(register))
    .map(([kind]) => kind);
}
